import { cx } from "components/Graphs/utils";
import React from "react";

import type { ApplicationBalanceModel } from "../ApplicationBalance/model";

export interface BalanceDetailsProps {
  model: ApplicationBalanceModel;
  className?: string;
}

const toneTextClasses = {
  healthy: "text-green-400",
  warning: "text-amber-300",
  critical: "text-red-400",
};

const BreakdownRow = ({ label, value }: { label: string; value: number }) => (
  <div className="flex items-baseline gap-1">
    <span className="text-zinc-400">{label}</span>
    <span className="mb-[3px] flex-1 border-b border-dotted border-zinc-600" />
    <span className="font-mono text-[13px] text-zinc-300">
      ${value.toFixed(2)}/hr
    </span>
  </div>
);

export const BalanceDetails: React.FC<BalanceDetailsProps> = ({
  model,
  className,
}) => {
  const { burnBreakdown } = model;
  const showGrantHeadline = Boolean(model.grantHeadline);
  const hasGpuDetails =
    !showGrantHeadline &&
    model.currencyDisplay === "hours" &&
    model.mostExpensiveInstanceType &&
    model.leastExpensiveInstanceType;
  const hasBurnDetails = !showGrantHeadline && burnBreakdown.total > 0;

  return (
    <div className={cx("w-64 text-sm text-white", className)}>
      {showGrantHeadline ? (
        <p className="leading-relaxed text-zinc-400">{model.grantHeadline}</p>
      ) : null}

      {showGrantHeadline && (hasGpuDetails || hasBurnDetails) ? (
        <div className="my-2 border-t border-zinc-700" />
      ) : null}

      {hasGpuDetails ? (
        <section aria-label="GPU hours estimate">
          <p className="leading-relaxed text-zinc-400">
            Balance based on potential usage of:
          </p>
          <ul className="mt-1 list-inside list-disc">
            <li className="text-zinc-300">
              {model.mostExpensiveInstanceType}{" "}
              <span className="text-zinc-400">for</span>{" "}
              {model.gpuHoursMostExpensive} hrs
            </li>
            <li className="text-zinc-300">
              {model.leastExpensiveInstanceType}{" "}
              <span className="text-zinc-400">for</span>{" "}
              {model.gpuHoursLeastExpensive} hrs
            </li>
          </ul>
        </section>
      ) : null}

      {hasGpuDetails && hasBurnDetails ? (
        <div className="my-2 border-t border-zinc-700" />
      ) : null}

      {hasBurnDetails ? (
        <section aria-label="Balance burn rate">
          <div
            className={cx(
              "flex gap-4",
              model.currencyDisplay === "credits" &&
                model.currentBalance > 0 &&
                "justify-between",
            )}
          >
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-400">
                Burn rate
              </p>
              <p className="text-sm font-semibold text-white">
                ${burnBreakdown.total.toFixed(2)}
                <span className="font-normal text-zinc-400"> / hr</span>
              </p>
            </div>
            {model.currencyDisplay === "credits" && model.currentBalance > 0 ? (
              <>
                <div className="self-stretch border-l border-zinc-700" />
                <div className="text-right">
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-400">
                    Credits left
                  </p>
                  <p
                    className={cx(
                      "text-sm font-semibold",
                      model.tone && toneTextClasses[model.tone],
                    )}
                  >
                    {(model.currentBalance / burnBreakdown.total).toFixed(1)}
                    <span className="text-xs font-normal text-zinc-400">
                      {" "}
                      hrs
                    </span>
                  </p>
                </div>
              </>
            ) : null}
          </div>

          {burnBreakdown.computeCost > 0 || burnBreakdown.storageCost > 0 ? (
            <>
              <div className="my-2 border-t border-zinc-700" />
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-400">
                All environments
              </p>
              <div className="flex flex-col gap-1">
                {burnBreakdown.computeCost > 0 ? (
                  <BreakdownRow
                    label="Compute"
                    value={burnBreakdown.computeCost}
                  />
                ) : null}
                {burnBreakdown.storageCost > 0 ? (
                  <BreakdownRow
                    label="Storage"
                    value={burnBreakdown.storageCost}
                  />
                ) : null}
              </div>
            </>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

BalanceDetails.displayName = "BalanceDetails";

export default BalanceDetails;
